import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import * as Mount from "foldkit/mount";
import { defineView } from "foldkit/submodel";
import * as lucide from "lucide";
import * as Relationships from "../../widgets/relationships";
import { tabsOf } from "../../widgets/tabs";

import type { Entry } from "../../api/schemas/entries";
import type { Backlink } from "../../api/schemas/notes";
import { Cadence, type RecordSummary } from "../../api/schemas/records";
import type { Task as PersonTask } from "../../api/schemas/tasks";
import { Route, linkHref } from "../../app/routes";
import { type Task, parseTasks } from "../../core/recordMemory";
import { i18next, recordSubtypeLabel } from "../../i18n";
import { gradeChip, priorityGlyph, statusChip } from "../../widgets/a2aUi";
import * as AddEntry from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import { avatarView } from "../../widgets/avatar";
import { CropImage, PreviewBlob } from "../../widgets/cropper";
import { AlertCircle, ChevronDown, icon } from "../../widgets/icons";
import { lastEditView, provenanceView } from "../../widgets/provenance";
import * as Tags from "../../widgets/tags";
import * as ValidatedField from "../../widgets/validatedField";
import { tagSuggestions } from "../entry/update";
import { parseTags, sourceBadgeClass, versionAt } from "../history";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { EditForm, HistoryEntry, ImageModal, Model, Tab } from "./model";
import {
	entryItems,
	errorText,
	recordSchema,
	resolveSubtitle,
	storedAgentLink,
} from "./update";

/** v1 `format(date, "yyyy-MM-dd HH:mm:ss")`; empty when unparsable. */
export const formatTimestamp = (iso: string): string => {
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

type UploadFile = { name: string; size: number; uploadedAt?: string };
const latestUploadFile = (entry: Entry): UploadFile | null => {
	if (entry.type !== "upload" || !entry.json) return null;
	try {
		const parsed = JSON.parse(entry.json) as {
			files?: unknown;
			file?: unknown;
		};
		const files = Array.isArray(parsed.files)
			? parsed.files
			: parsed.file
				? [parsed.file]
				: [];
		const last = files[files.length - 1];
		return typeof last === "object" &&
			last !== null &&
			typeof (last as UploadFile).name === "string"
			? (last as UploadFile)
			: null;
	} catch {
		return null;
	}
};

/** v1 `displayTitle`: primary task, title, a 25-char description preview, then a placeholder. */
export const displayTitle = (
	entry: Entry,
	tasks: ReadonlyArray<Task> = parseTasks(entry),
): string => {
	const task = tasks[0]?.text.trim() ?? "";
	if (task !== "") return task;
	const title = entry.title.trim();
	if (title !== "") return title;
	const description = entry.description.trim();
	if (description !== "")
		return (description.split("\n")[0] ?? "").slice(0, 25).trim();
	return entry.type === "upload" ? "Upload" : "Untitled";
};

/** v1 `knotTypeIconMap` (lucide, size 16); `StickyNote` for notes and unknown types. */
const entryIcon = (h: HtmlBuilder<Message>, type: string): Html => {
	const names: Record<string, keyof typeof lucide> = {
		upload: "FileDown",
		tasks: "CheckSquare",
		update: "RefreshCw",
		learning: "Lightbulb",
		risk: "AlertTriangle",
		decision: "Scale",
		accomplishment: "Trophy",
		hypothesis: "HelpCircle",
		experiment: "FlaskConical",
	};
	const name = names[type] ?? "StickyNote";
	return icon(h, name, lucide[name] as typeof lucide.StickyNote, {
		size: 16,
	});
};

/** Date and time in the user's locale; raw string when unparsable. */
export const formatHistoryDate = (iso: string): string => {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const avatar = (
	h: HtmlBuilder<Message>,
	model: Model,
	name: string,
	size = "w-24 h-24",
): Html =>
	avatarView(h, model.image, name, {
		size,
		textSize: "text-4xl",
		alt: `${name}'s avatar`,
	});

/** v1 `ProfileImageModal` + `ProfileImageUploader` (react-easy-crop stage replaced by the cropper Mount). */
const imageModalView = (h: HtmlBuilder<Message>, modal: ImageModal): Html =>
	h.div(
		[
			h.Class(
				"fixed inset-0 bg-black/50 flex items-center justify-center z-50",
			),
		],
		[
			h.div(
				[
					h.Role("dialog"),
					h.AriaModal(true),
					h.AriaLabel("Upload Profile Image"),
					h.Class("bg-base-100 rounded-lg p-6 max-w-2xl w-full mx-4"),
				],
				[
					h.div(
						[h.Class("flex justify-between items-center mb-4")],
						[
							h.h3(
								[h.Class("text-lg font-semibold")],
								["Upload Profile Image"],
							),
							h.button(
								[
									h.Type("button"),
									h.Class("text-base-content/60 hover:text-base-content/80"),
									h.AriaLabel("Close"),
									h.OnClick(Message.ClickedCloseImageModal()),
								],
								["✕"],
							),
						],
					),
					h.div(
						[h.Class("p-4 max-w-md flex flex-col items-center")],
						[
							h.input([
								h.Type("file"),
								h.Accept("image/jpeg, image/png"),
								h.AriaLabel("Choose an image"),
								h.Class(
									"block mb-4 text-sm text-base-content/80 file:mr-2 file:py-2 file:px-4 file:border file:border-base-300 file:rounded-sm file:bg-base-100",
								),
								h.Disabled(modal.uploading),
								h.OnFileChange((files) =>
									Message.ChangedImageFile({
										file: Option.fromNullishOr(files[0]),
									}),
								),
							]),
							modal.stage._tag === "Crop"
								? h.div(
										[
											h.DataAttribute("testid", "cropper-host"),
											h.Class("flex flex-col items-center"),
											h.OnMount(
												Mount.mapMessage(
													CropImage({ file: modal.stage.file }),
													(message) => Message.GotCropMessage({ message }),
												),
											),
										],
										[],
									)
								: h.empty,
							modal.stage._tag === "Preview"
								? h.div(
										[h.Class("mt-4")],
										[
											h.img([
												h.Alt("Cropped Preview"),
												h.Class("w-32 h-32 object-cover rounded-full border"),
												h.OnMount(
													Mount.mapMessage(
														PreviewBlob({ blob: modal.stage.blob }),
														() => Message.CompletedPreview(),
													),
												),
											]),
											h.div(
												[h.Class("mt-3 flex gap-3")],
												[
													h.button(
														[
															h.Type("button"),
															h.Class("btn"),
															h.AriaLabel("Back to Crop"),
															h.Disabled(modal.uploading),
															h.OnClick(Message.ClickedBackToCrop()),
														],
														["Back to Crop"],
													),
													h.button(
														[
															h.Type("button"),
															h.Class("btn btn-primary"),
															h.AriaLabel("Upload"),
															h.Disabled(modal.uploading || modal.uploaded),
															h.OnClick(Message.ClickedUpload()),
														],
														[
															modal.uploading
																? h.span(
																		[
																			h.Class(
																				"loading loading-spinner -ml-1 mr-2 h-4 w-4",
																			),
																		],
																		[],
																	)
																: modal.uploaded
																	? h.span([h.Class("w-4 h-4 mr-2")], ["✓"])
																	: h.empty,
															modal.uploading
																? "Uploading..."
																: modal.uploaded
																	? "Uploaded!"
																	: "Upload",
														],
													),
												],
											),
											Option.match(modal.error, {
												onNone: () => h.empty,
												onSome: (error) =>
													h.div(
														[
															h.Role("alert"),
															h.Class("mt-3 text-error text-sm"),
														],
														[error],
													),
											}),
										],
									)
								: Option.match(modal.error, {
										onNone: () => h.empty,
										onSome: (error) =>
											h.div(
												[h.Role("alert"), h.Class("mt-3 text-error text-sm")],
												[error],
											),
									}),
						],
					),
				],
			),
		],
	);

const editView = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	record: RecordSummary,
	form: EditForm,
): Html =>
	h.div(
		[
			h.Class(
				"card card-side bg-base-100 border w-full max-w-5xl mx-auto my-6",
			),
		],
		[
			h.div(
				[h.Class("card-body")],
				[
					Option.match(model.imageModal, {
						onNone: () => h.empty,
						onSome: (modal) => imageModalView(h, modal),
					}),
					h.form(
						[
							h.AriaLabel("Edit profile"),
							h.Class("flex flex-col gap-4"),
							h.OnSubmit(Message.SubmittedEdit()),
						],
						[
							Option.match(form.error, {
								onNone: () => h.empty,
								onSome: (error) =>
									h.div(
										[h.Role("alert"), h.Class("alert alert-error")],
										[
											h.span([], [error]),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-sm btn-ghost"),
													h.AriaLabel("Dismiss error"),
													h.OnClick(Message.ClickedDismissError()),
												],
												["✕"],
											),
										],
									),
							}),
							h.div(
								[h.Class("flex flex-col md:flex-row gap-8")],
								[
									h.div(
										[h.Class("md:w-1/3 flex flex-col items-center gap-3")],
										[
											avatar(h, model, record.name, "w-48 h-48"),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-outline w-full md:w-auto"),
													h.AriaLabel("Change Profile Image"),
													h.OnClick(Message.ClickedChangeImage()),
												],
												["Change Profile Image"],
											),
										],
									),
									h.div(
										[h.Class("md:w-2/3 flex flex-col gap-4")],
										[
											ValidatedField.textInput(
												h,
												"edit-name",
												"Name",
												form.name,
												(value) => Message.ChangedName({ value }),
												"text",
												undefined,
												ValidatedField.RECORD_FIELD_MAX.name,
											),
											ValidatedField.textInput(
												h,
												"edit-subtitle",
												"Subtitle",
												form.subtitle,
												(value) => Message.ChangedSubtitle({ value }),
												"text",
												undefined,
												ValidatedField.RECORD_FIELD_MAX.subtitle,
											),
											ValidatedField.textInput(
												h,
												"edit-email",
												"Email",
												form.email,
												(value) => Message.ChangedEmail({ value }),
												"email",
												undefined,
												ValidatedField.RECORD_FIELD_MAX.email,
											),
											h.div(
												[],
												[
													h.label(
														[h.Class("block font-semibold")],
														["Custom Attributes"],
													),
													Attributes.view(h, {
														rows: form.attributes,
														schema: recordSchema(shared),
														toParentMessage: (message) =>
															Message.GotAttributesMessage({ message }),
													}),
												],
											),
											h.div(
												[],
												[
													h.label(
														[
															h.For("edit-description"),
															h.Class("block font-semibold"),
														],
														["Description"],
													),
													h.textarea([
														h.Id("edit-description"),
														h.Maxlength(
															ValidatedField.RECORD_FIELD_MAX.description,
														),
														h.Class(
															`textarea textarea-bordered w-full min-h-24${form.description._tag === "Invalid" ? " textarea-error" : ""}`,
														),
														h.Value(form.description.value),
														h.OnInput((value) =>
															Message.ChangedDescription({ value }),
														),
													]),
													ValidatedField.error(h, form.description),
												],
											),
											h.div(
												[],
												[
													h.label([h.Class("block font-semibold")], ["Tags"]),
													Tags.view(h, {
														slotId: "edit-record-tags",
														model: form.tagBox,
														selected: form.tags,
														suggestions: tagSuggestions(shared),
														ariaLabel: "Tags",
														placeholder: "Add a tag and press Enter",
														error: form.tagError,
														toParentMessage: (message) =>
															Message.GotTagsMessage({ message }),
														onRemove: (tag) =>
															Message.ClickedRemoveTag({ tag }),
													}),
												],
											),
										],
									),
								],
							),
							h.div(
								[h.Class("flex justify-end mt-4")],
								[
									h.button(
										[
											h.Type("submit"),
											h.Class("btn btn-primary"),
											h.AriaLabel("Save"),
											h.Disabled(model.saving || form.name._tag === "Invalid"),
										],
										[
											model.saving
												? h.span([h.Class("loading loading-spinner")], [])
												: h.empty,
											"Save",
										],
									),
								],
							),
						],
					),
				],
			),
		],
	);

const toCadence = (value: string): Cadence | null =>
	Cadence.literals.find((c) => c === value) ?? null;

/** U8: cadence select and pause toggle, their own write outside the edit form (R19). */
const cadenceControls = (
	h: HtmlBuilder<Message>,
	model: Model,
	record: RecordSummary,
): ReadonlyArray<Html> => {
	const paused = record.paused ?? false;
	const pauseText = i18next.t(paused ? "record.unpause" : "record.pause");
	return [
		h.select(
			[
				h.Class("select select-bordered select-sm"),
				h.AriaLabel(i18next.t("record.cadence.label")),
				h.Value(record.cadence ?? ""),
				h.OnChange((value) =>
					Message.ChangedCadence({ cadence: toCadence(value) }),
				),
			],
			[
				h.option(
					[h.Value(""), h.Selected(record.cadence === undefined)],
					[i18next.t("record.cadence.none")],
				),
				...Cadence.literals.map((cadence) =>
					h.option(
						[h.Value(cadence), h.Selected(record.cadence === cadence)],
						[i18next.t(`keepInTouch.cadence.${cadence}`)],
					),
				),
			],
		),
		h.button(
			[
				h.Type("button"),
				h.Class(paused ? "btn btn-sm" : "btn btn-ghost btn-sm"),
				h.AriaLabel(pauseText),
				h.OnClick(Message.ClickedPause()),
			],
			[pauseText],
		),
		Option.match(model.cadenceError, {
			onNone: () => h.empty,
			onSome: (error) =>
				h.p([h.Role("alert"), h.Class("text-error text-xs")], [error]),
		}),
	];
};

/** L5: someone else's agent link on a person: paste, save, clear (its own write, like cadence). */
const agentLinkControl = (
	h: HtmlBuilder<Message>,
	model: Model,
	record: RecordSummary,
): Html => {
	const stored = storedAgentLink(record);
	return h.form(
		[
			h.Class("pt-3 border-t border-base-200 flex flex-col gap-1.5"),
			h.DataAttribute("testid", "record-agent-link"),
			h.OnSubmit(Message.SubmittedAgentLink()),
		],
		[
			h.label(
				[h.For("record-agent-link"), h.Class("text-sm font-semibold")],
				[i18next.t("record.agentLink.label")],
			),
			h.p(
				[
					h.Id("record-agent-link-hint"),
					h.Class("text-xs text-base-content/60"),
				],
				[i18next.t("record.agentLink.hint")],
			),
			h.div(
				[h.Class("flex flex-wrap items-center gap-2")],
				[
					h.input([
						h.Id("record-agent-link"),
						h.Type("url"),
						h.Class("input input-bordered input-sm flex-1 min-w-0"),
						h.Placeholder(i18next.t("record.agentLink.placeholder")),
						h.AriaDescribedBy("record-agent-link-hint"),
						h.Value(Option.getOrElse(model.agentLinkDraft, () => stored)),
						h.OnInput((value) => Message.ChangedAgentLink({ value })),
					]),
					h.button(
						[
							h.Type("submit"),
							h.Class("btn btn-primary btn-sm"),
							h.AriaLabel(i18next.t("record.agentLink.save")),
						],
						[i18next.t("record.agentLink.saveShort")],
					),
					stored === ""
						? h.empty
						: h.button(
								[
									h.Type("button"),
									h.Class("btn btn-ghost btn-sm"),
									h.AriaLabel(i18next.t("record.agentLink.clear")),
									h.OnClick(Message.ClickedClearAgentLink()),
								],
								[i18next.t("record.agentLink.clearShort")],
							),
				],
			),
			Option.match(model.agentLinkError, {
				onNone: () => h.empty,
				onSome: (error) =>
					h.p([h.Role("alert"), h.Class("text-error text-xs")], [error]),
			}),
		],
	);
};

const profileCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	record: RecordSummary,
): Html => {
	const subtitle = resolveSubtitle(record);
	const custom = Object.entries(record.attributes ?? {}).filter(
		([key]) => !Attributes.RESERVED_KEYS.has(key),
	);
	return h.div(
		[
			h.Class(
				"card bg-base-100 border border-base-300 shadow-sm w-full my-6 flex-row gap-4 p-4 sm:gap-6 sm:p-6",
			),
		],
		[
			h.div([h.Class("shrink-0")], [avatar(h, model, record.name)]),
			h.div(
				[h.Class("flex flex-col gap-3 min-w-0 grow")],
				[
					h.div(
						// Below sm the controls drop under the name instead of pushing past the card (U5: 390px).
						[
							h.Class(
								"flex flex-col items-start justify-between gap-4 sm:flex-row",
							),
						],
						[
							h.div(
								[h.Class("flex flex-col gap-1 min-w-0")],
								[
									h.div(
										[h.Class("flex flex-wrap items-center gap-2")],
										[
											h.h2(
												[
													h.Class(
														"text-2xl font-bold tracking-tight leading-tight",
													),
												],
												[record.name],
											),
											h.span(
												[h.Class("badge badge-outline badge-sm")],
												[recordSubtypeLabel(record.type)],
											),
										],
									),
									subtitle
										? h.p([h.Class("text-base-content/60")], [subtitle])
										: h.empty,
									// R16, R17: who created the record, and who last edited it.
									h.div(
										[
											h.Class(
												"flex flex-wrap items-center gap-3 text-xs text-base-content/60",
											),
											h.DataAttribute("testid", "record-provenance"),
										],
										[
											provenanceView(h, record.provenance),
											lastEditView(h, record.lastEdit),
										],
									),
								],
							),
							Option.isSome(shared.asOf)
								? h.empty
								: h.div(
										[h.Class("flex flex-wrap items-center gap-2 sm:shrink-0")],
										[
											...cadenceControls(h, model, record),
											h.button(
												[
													h.Type("button"),
													h.Class("btn btn-outline btn-sm"),
													h.AriaLabel("Edit profile"),
													h.OnClick(Message.ClickedEdit()),
												],
												["Edit"],
											),
										],
									),
						],
					),
					record.description
						? h.p(
								[h.Class("leading-relaxed text-base-content/80")],
								[record.description],
							)
						: h.empty,
					record.tags.length > 0
						? h.div(
								[h.Class("flex flex-wrap gap-1.5")],
								record.tags.map((tag) =>
									h.div(
										[h.Key(tag), h.Class("badge badge-soft badge-primary")],
										[tag],
									),
								),
							)
						: h.empty,
					custom.length > 0
						? h.div(
								[h.Class("pt-3 border-t border-base-200")],
								[
									h.h3([h.Class("sr-only")], ["Attributes"]),
									h.dl(
										[h.Class("grid grid-cols-2 md:grid-cols-3 gap-3")],
										custom.map(([key, value]) =>
											h.keyed("div")(
												key,
												[h.Class("flex flex-col gap-0.5 min-w-0")],
												[
													h.dt(
														[
															h.Class(
																"text-[11px] font-semibold uppercase tracking-wider text-base-content/50",
															),
														],
														[key],
													),
													h.dd([h.Class("text-sm truncate")], [`${value}`]),
												],
											),
										),
									),
								],
							)
						: h.empty,
					shared.a2aContextEnabled &&
					record.type === "person" &&
					Option.isNone(shared.asOf)
						? agentLinkControl(h, model, record)
						: h.empty,
					record.lastUpdated
						? h.p(
								[h.Class("text-xs text-base-content/50")],
								[`Last updated: ${record.lastUpdated}`],
							)
						: h.empty,
				],
			),
		],
	);
};

/**
 * U7 SEAM: the description renders as plain text here. U7 replaces
 * `descriptionView` with the sanitized markdown renderer (KTD6).
 */
const descriptionView = (h: HtmlBuilder<Message>, entry: Entry): Html =>
	entry.description.trim() === ""
		? h.empty
		: h.p(
				[
					h.Class(
						"whitespace-pre-wrap text-sm leading-relaxed text-base-content/80",
					),
				],
				[entry.description],
			);

/** v1 `renderMetaRow` (without the edit/delete controls, which the Record page does not port). */
const metaRow = (
	h: HtmlBuilder<Message>,
	entry: Entry,
	tags: ReadonlyArray<string>,
): Html => {
	const timestamp = formatTimestamp(entry.timestamp);
	return h.div(
		[
			h.Class(
				"flex w-full flex-wrap items-center gap-2 text-xs text-base-content/60",
			),
		],
		[
			h.div(
				[h.Class("flex flex-wrap items-center gap-2")],
				[
					timestamp
						? h.time([h.Class("font-mono text-base-content/50")], [timestamp])
						: h.empty,
					// R16: the creating write; `author` stands in for older responses.
					provenanceView(h, entry.provenance, entry.author),
					tags.length > 0
						? h.div(
								[h.Class("flex flex-wrap items-center gap-1")],
								tags.map((tag) =>
									h.span(
										[h.Key(tag), h.Class("badge badge-outline badge-xs")],
										[`#${tag}`],
									),
								),
							)
						: h.empty,
				],
			),
			h.div([h.Class("ml-auto flex items-center gap-2 text-base-content")], []),
		],
	);
};

const entryView = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	entry: Entry,
	isLast: boolean,
): Html => {
	const tasks = parseTasks(entry);
	const primary = tasks[0];
	const file = latestUploadFile(entry);
	const tags = Array.from(
		new Set(entry.tags.filter((tag) => tag.trim() !== "")),
	);
	const href = h.Href(
		linkHref(shared.route, Route.Entry({ id: entry.id, asOf: Option.none() })),
	);
	const title = displayTitle(entry, tasks);
	const heading =
		entry.type === "tasks"
			? h.div(
					[h.Class("flex flex-col gap-2")],
					[
						h.div(
							[h.Class("flex flex-wrap items-start gap-3")],
							[
								h.div(
									[h.Class("flex items-start gap-2 w-full")],
									[
										primary
											? h.input([
													h.Type("checkbox"),
													h.Class("checkbox checkbox-primary mt-1"),
													h.Checked(primary.completed),
													h.Disabled(true),
													h.AriaLabel(
														primary.completed
															? "Mark task incomplete"
															: "Mark task complete",
													),
												])
											: h.empty,
										h.div(
											[h.Class("flex flex-col gap-1 w-full ")],
											[
												h.a(
													[
														href,
														h.Class(
															`text-base font-semibold leading-tight ${
																primary?.completed
																	? "text-base-content/60 line-through"
																	: "text-base-content transition-colors hover:text-primary"
															}`,
														),
													],
													[title],
												),
												metaRow(h, entry, tags),
											],
										),
									],
								),
							],
						),
					],
				)
			: h.div(
					[h.Class("flex flex-col gap-2")],
					[
						h.div(
							[h.Class("flex flex-wrap items-start gap-3")],
							[
								h.div(
									[h.Class("flex flex-col gap-1 w-full")],
									[
										h.a(
											[
												href,
												h.Class(
													"text-base font-semibold leading-tight text-base-content transition-colors hover:text-primary",
												),
											],
											[title],
										),
										metaRow(h, entry, tags),
									],
								),
							],
						),
					],
				);
	return h.keyed("li")(
		entry.id,
		[h.DataAttribute("testid", "timeline-entry")],
		[
			h.hr([]),
			h.div(
				[h.Class("timeline-middle")],
				[
					h.div(
						[
							h.Class(
								"flex h-8 w-8 items-center justify-center rounded-full border border-base-300 bg-base-100 text-base-content/70",
							),
						],
						[entryIcon(h, entry.type)],
					),
				],
			),
			h.div(
				[
					h.Class(
						"timeline-end mb-6 w-full rounded-xl border border-base-300 bg-base-100 p-4 flex flex-col gap-3",
					),
				],
				[
					heading,
					entry.type === "upload"
						? h.div(
								[h.Class("rounded-lg bg-base-200/60 p-3")],
								file
									? [
											h.div(
												[h.Class("flex flex-col gap-3")],
												[
													h.div(
														[],
														[
															h.div([h.Class("font-medium")], [file.name]),
															h.div(
																[h.Class("text-sm text-base-content/70")],
																[
																	`${AddEntry.formatFileSize(file.size)}${file.uploadedAt ? ` • Uploaded ${formatTimestamp(file.uploadedAt)}` : ""}`,
																],
															),
														],
													),
													entry.description.trim() === ""
														? h.empty
														: h.p(
																[h.Class("text-sm text-base-content/80")],
																[entry.description],
															),
												],
											),
										]
									: [
											h.span(
												[h.Class("text-sm text-error")],
												["File details unavailable"],
											),
										],
							)
						: entry.type === "tasks"
							? h.empty
							: descriptionView(h, entry),
				],
			),
			isLast ? h.empty : h.hr([]),
		],
	);
};

// U12: Tasks and Notes tabs (person records, behind `a2aContextEnabled`).

/** "Tasks" before the fetch lands, "Tasks (2)" after. */
const countedLabel = <A>(
	label: string,
	data: AsyncData.AsyncData<A, unknown>,
	count: (a: A) => number | string,
): string =>
	AsyncData.isSuccess(data)
		? i18next.t("record.tabs.counted", { label, count: count(data.data) })
		: label;

const liveList = <A>(
	h: HtmlBuilder<Message>,
	shared: Shared,
	data: AsyncData.AsyncData<A, unknown>,
	items: (a: A) => ReadonlyArray<Html>,
	texts: { empty: string; failed: string },
	footer: (a: A) => Html | null = () => null,
): Html => {
	const notice = Option.isSome(shared.asOf)
		? h.p(
				[h.Class("text-sm text-base-content/60 mb-3")],
				[i18next.t("record.tabs.liveNotice")],
			)
		: h.empty;
	if (AsyncData.isFailure(data))
		return h.div(
			[h.Role("alert"), h.Class("alert alert-error")],
			[texts.failed],
		);
	if (!AsyncData.isSuccess(data))
		return h.div(
			[h.Role("status"), h.Class("w-full text-center")],
			[h.span([h.Class("loading loading-dots loading-lg")], [])],
		);
	const rows = items(data.data);
	return h.div(
		[],
		[
			notice,
			rows.length === 0
				? h.p([h.Class("text-sm text-base-content/60")], [texts.empty])
				: h.ul(
						[
							h.Class(
								"divide-y divide-base-300 rounded-box border border-base-300 bg-base-100",
							),
						],
						rows,
					),
			footer(data.data) ?? h.empty,
		],
	);
};

/** "Oct 22" (or "Oct 22, 2027" outside the read year), in UTC like the Tasks list. */
const dueText = (due: string, now: number): string => {
	const ms = Date.parse(due.length === 10 ? `${due}T00:00:00Z` : due);
	if (Number.isNaN(ms)) return due;
	const sameYear =
		new Date(ms).getUTCFullYear() === new Date(now).getUTCFullYear();
	return new Intl.DateTimeFormat(i18next.language, {
		timeZone: "UTC",
		month: "short",
		day: "numeric",
		...(sameYear ? {} : { year: "numeric" }),
	}).format(ms);
};

const dueChip = (
	h: HtmlBuilder<Message>,
	task: PersonTask,
	now: number,
): Html => {
	if (task.due === null) return h.empty;
	const overdue =
		now > 0 &&
		task.status !== "done" &&
		task.status !== "canceled" &&
		task.due.slice(0, 10) < new Date(now).toISOString().slice(0, 10);
	return h.span(
		[
			h.DataAttribute("testid", "person-task-due"),
			h.Class(
				`text-xs tabular-nums ${overdue ? "text-error font-medium" : "text-base-content/60"}`,
			),
		],
		[
			i18next.t(overdue ? "tasks.list.overdue" : "tasks.list.due", {
				date: dueText(task.due, now),
			}),
		],
	);
};

/** Title on its own line below `sm` (status and meta wrap under it); one line from `sm` up. */
const taskRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	task: PersonTask,
	now: number,
): Html =>
	h.li(
		[
			h.DataAttribute("testid", "person-task"),
			h.Class("flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3"),
		],
		[
			h.a(
				[
					h.Href(
						linkHref(
							shared.route,
							Route.Task({ id: task.id, asOf: shared.asOf }),
						),
					),
					h.Class(
						`link link-hover font-medium min-w-0 basis-full sm:order-2 sm:basis-0 sm:flex-1 ${task.status === "canceled" ? "line-through" : ""}`,
					),
				],
				[task.title],
			),
			h.span([h.Class("sm:order-1")], [statusChip(h, task.status)]),
			h.span(
				[h.Class("flex flex-wrap items-center gap-x-3 gap-y-1 sm:order-3")],
				[
					priorityGlyph(h, task.priority),
					dueChip(h, task, now),
					gradeChip(h, task.grade),
				],
			),
		],
	);

/**
 * The server's snippet is raw markdown with whitespace collapsed, sometimes a
 * window cut mid-text (marked by a leading `…`). Drop a leading `# <title>`
 * that repeats the note title, or the cut-off tail of it; turn the remaining
 * headings into ` · ` breaks so they don't run into the body text; strip
 * emphasis, inline code ticks and `[text](url)` down to its text.
 */
const plainSnippet = (snippet: string, title: string): string => {
	const ellipsis = snippet.startsWith("…") ? "…" : "";
	const body = snippet.slice(ellipsis.length).trimStart();
	const heading = `# ${title}`;
	// A cut window may start partway into the title: match its longest
	// word-aligned tail. Uncut snippets only drop the whole heading.
	const repeat = !title
		? ""
		: ellipsis
			? (heading
					.split("")
					.map((_, i) => heading.slice(i))
					.find(
						(tail, i) =>
							(i === 0 || heading[i - 1] === " ") &&
							tail.trim().length > 0 &&
							body.startsWith(tail),
					) ?? "")
			: body.startsWith(heading)
				? heading
				: "";
	const text = body
		.slice(repeat.length)
		.replace(/^\s*#{1,6}\s+/, "")
		.replace(/\s+#{1,6}\s+/g, " · ")
		.replace(/\[([^\]\n]+)\]\([^)\s]*\)/g, "$1")
		.replace(/(\*\*|__|`)/g, "")
		.trim();
	return repeat && ellipsis ? text : ellipsis + text;
};

/** `[[link]]` and `#tag` set off as plain text spans; never HTML. */
const snippetParts = (
	h: HtmlBuilder<Message>,
	text: string,
): ReadonlyArray<Html | string> =>
	text
		.split(/(\[\[[^[\]\n]+\]\]|(?<![\w#])#[\w-]+)/)
		.map((part, i) =>
			i % 2 === 1
				? h.span(
						[h.Class("font-medium text-base-content")],
						[part.startsWith("[[") ? part.slice(2, -2) : part],
					)
				: part,
		);

const backlinkRow = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	note: Backlink,
): Html =>
	h.li(
		[h.DataAttribute("testid", "person-note"), h.Class("px-4 py-3")],
		[
			h.a(
				[
					h.Href(
						linkHref(
							shared.route,
							Route.Note({ id: note.id, asOf: shared.asOf }),
						),
					),
					h.Class("link link-hover font-medium"),
				],
				[note.title || i18next.t("record.tabs.untitledNote")],
			),
			note.snippet
				? h.p(
						[h.Class("text-sm text-base-content/70 mt-1")],
						snippetParts(h, plainSnippet(note.snippet, note.title)),
					)
				: h.empty,
		],
	);

const historyView = (h: HtmlBuilder<Message>, model: Model): Html => {
	if (AsyncData.isLoading(model.history)) {
		return h.div(
			[h.Role("status"), h.Class("w-full text-center py-8")],
			[h.span([h.Class("loading loading-dots loading-lg")], [])],
		);
	}
	if (AsyncData.isFailure(model.history)) {
		return h.div(
			[h.Role("alert"), h.Class("text-center py-8 text-error")],
			[i18next.t("history.failedToLoad")],
		);
	}
	const history = AsyncData.getOrElse(model.history, () => ({
		items: [] as ReadonlyArray<HistoryEntry>,
		nextCursor: Option.none<number>(),
	}));
	if (history.items.length === 0) {
		return h.div(
			[h.Class("text-center py-8 text-base-content/60")],
			[i18next.t("history.noHistory")],
		);
	}
	return h.div(
		[h.Class("flex flex-col gap-3")],
		[
			...history.items.map((entry, index) => {
				const tags = parseTags(entry.version.tags);
				const versionNumber = versionAt(history.items.length, index);
				const cardId = `history-version-${versionNumber}`;
				const key = `${entry.validFrom}-${index}`;
				const expanded = model.expandedHistory.includes(key);
				// v1 `HistoryVersionCard`
				return h.keyed("div")(
					key,
					[h.Class("card bg-base-100 border border-base-300 shadow-sm")],
					[
						h.button(
							[
								h.Type("button"),
								h.Class("card-body p-4 cursor-pointer text-left w-full"),
								h.AriaExpanded(expanded),
								h.AriaControls(`${cardId}-content`),
								h.OnClick(Message.ToggledHistoryCard({ id: key })),
							],
							[
								h.div(
									[h.Class("flex items-center justify-between")],
									[
										h.div(
											[h.Class("flex items-center gap-2")],
											[
												h.span(
													[h.Class("font-semibold text-base-content")],
													[`${i18next.t("history.version")} ${versionNumber}`],
												),
												entry.validTo === null
													? h.span(
															[h.Class("badge badge-success badge-sm")],
															[i18next.t("history.current")],
														)
													: h.empty,
												h.span(
													[
														h.Class(
															`badge badge-sm ${sourceBadgeClass(entry.event.source)}`,
														),
													],
													[entry.event.source],
												),
												// R19: the same label and popover per version.
												provenanceView(h, entry.event.provenance, null, {
													channelBadge: false,
												}),
											],
										),
										h.div(
											[h.Class("flex items-center gap-2")],
											[
												h.span(
													[h.Class("text-sm text-base-content/60")],
													[formatHistoryDate(entry.validFrom)],
												),
												ChevronDown(h, {
													size: 16,
													class: `transition-transform ${expanded ? "rotate-180" : ""}`,
												}),
											],
										),
									],
								),
								entry.validTo
									? h.div(
											[h.Class("text-xs text-base-content/50 mt-1")],
											[
												`${i18next.t("history.validUntil")} ${formatHistoryDate(entry.validTo)}`,
											],
										)
									: h.empty,
							],
						),
						expanded
							? h.div(
									[h.Id(`${cardId}-content`), h.Class("px-4 pb-4")],
									[
										h.div([h.Class("divider my-0")], []),
										h.div(
											[h.Class("mt-3")],
											[
												h.div(
													[h.Class("space-y-2 text-sm")],
													[
														h.div(
															[],
															[
																h.span(
																	[h.Class("font-semibold")],
																	[`${i18next.t("history.name")}:`],
																),
																` ${entry.version.name}`,
															],
														),
														h.div(
															[],
															[
																h.span(
																	[h.Class("font-semibold")],
																	[`${i18next.t("history.type")}:`],
																),
																" ",
																h.span(
																	[h.Class("capitalize")],
																	[entry.version.type],
																),
															],
														),
														entry.version.description
															? h.div(
																	[],
																	[
																		h.span(
																			[h.Class("font-semibold")],
																			[`${i18next.t("history.description")}:`],
																		),
																		` ${entry.version.description}`,
																	],
																)
															: h.empty,
														tags.length > 0
															? h.div(
																	[
																		h.Class(
																			"flex flex-wrap items-center gap-1",
																		),
																	],
																	[
																		h.span(
																			[h.Class("font-semibold")],
																			[`${i18next.t("history.tags")}:`],
																		),
																		...tags.map((tag) =>
																			h.span(
																				[
																					h.Key(tag),
																					h.Class(
																						"badge badge-outline badge-xs",
																					),
																				],
																				[tag],
																			),
																		),
																	],
																)
															: h.empty,
													],
												),
											],
										),
										h.div(
											[h.Class("mt-3")],
											[
												h.button(
													[
														h.Type("button"),
														h.Class("btn btn-ghost btn-xs"),
														h.AriaLabel(`View at ${entry.validFrom}`),
														h.OnClick(
															Message.ClickedViewAt({
																validFrom: entry.validFrom,
															}),
														),
													],
													[i18next.t("history.viewAtThisPoint")],
												),
											],
										),
									],
								)
							: h.empty,
					],
				);
			}),
			Option.isSome(history.nextCursor)
				? h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm self-center"),
							h.AriaLabel("Load more history"),
							h.OnClick(Message.ClickedLoadMoreHistory()),
						],
						["Load more"],
					)
				: h.empty,
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const error = errorText(model, shared);
	if (error !== undefined) {
		return h.div(
			[
				h.DataAttribute("testid", "page-Record"),
				h.Role("alert"),
				h.Class("alert alert-error"),
			],
			[error],
		);
	}
	const record = AsyncData.getOrElse(model.record, () => null);
	if (record === null) {
		return h.div(
			[
				h.DataAttribute("testid", "page-Record"),
				h.Role("status"),
				h.Class("w-full text-center"),
			],
			[h.span([h.Class("loading loading-dots loading-lg")], [])],
		);
	}
	const personTabs = shared.a2aContextEnabled && record.type === "person";
	// A hidden tab (flag turned off) falls back to Entries.
	const selected: Tab =
		personTabs || model.tab === "entries" || model.tab === "history"
			? model.tab
			: "entries";
	const tabs = tabsOf(h, selected, (tab) => Message.SelectedTab({ tab }));
	const entries = entryItems(model);
	const timeTraveling = Option.isSome(shared.asOf);
	const nextCursor = AsyncData.getOrElse(model.entries, () => ({
		nextCursor: Option.none<number>(),
	})).nextCursor;
	return h.div(
		[h.DataAttribute("testid", "page-Record"), h.Class("max-w-3xl mx-auto")],
		[
			h.nav(
				[
					h.AriaLabel("Breadcrumb"),
					h.Class("flex items-center gap-2 text-sm text-base-content/60"),
				],
				[
					h.a(
						[
							h.Href(
								linkHref(
									shared.route,
									Route.Home({ asOf: Option.none(), q: Option.none() }),
								),
							),
							h.Class("hover:text-base-content"),
						],
						["Records"],
					),
					h.span([h.AriaHidden(true)], ["/"]),
					h.span([h.Class("text-base-content/80 truncate")], [record.name]),
				],
			),
			Option.match(model.edit, {
				onNone: () => profileCard(h, model, shared, record),
				onSome: (form) => editView(h, model, shared, record, form),
			}),
			record.type === "person"
				? h.submodel({
						slotId: "relationships",
						view: Relationships.view,
						model: model.relationships,
						toParentMessage: (message: Relationships.Message) =>
							Message.GotRelationshipsMessage({ message }),
						viewInputs: { ...shared, ownerName: record.name },
					})
				: h.empty,
			tabs.list([
				tabs.tab("entries", i18next.t("record.entries")),
				tabs.panel("entries", () =>
					h.div(
						[],
						[
							h.ul(
								[
									h.Class(
										"timeline timeline-snap-icon timeline-compact max-md:timeline-compact timeline-vertical",
									),
								],
								[
									timeTraveling
										? h.empty
										: h.submodel({
												slotId: "add-entry",
												view: AddEntry.view,
												model: model.addEntry,
												toParentMessage: (message: AddEntry.Message) =>
													Message.GotAddEntryMessage({ message }),
												viewInputs: {
													hasOtherEntries: entries.length > 0,
													dark: shared.theme === "dark",
													tagSuggestions: tagSuggestions(shared),
												},
											}),
									...entries.map((entry, i) =>
										entryView(h, shared, entry, i === entries.length - 1),
									),
								],
							),
							AsyncData.isRefreshing(model.entries) ||
							AsyncData.isLoading(model.entries)
								? h.div(
										[h.Role("status"), h.Class("w-full text-center mt-4")],
										[h.span([h.Class("loading loading-dots loading-lg")], [])],
									)
								: Option.isSome(nextCursor)
									? h.div(
											[h.Class("w-full text-center mt-4")],
											[
												h.button(
													[
														h.Type("button"),
														h.Class("btn btn-sm"),
														h.AriaLabel("Load more"),
														h.OnClick(Message.ClickedLoadMoreEntries()),
													],
													["Load more"],
												),
											],
										)
									: h.empty,
						],
					),
				),
				tabs.tab("history", i18next.t("common.history")),
				tabs.panel("history", () => historyView(h, model)),
				...(personTabs
					? [
							tabs.tab(
								"tasks",
								countedLabel(
									i18next.t("record.tabs.tasks"),
									model.tasks,
									(page) =>
										page.cursor === null
											? page.tasks.length
											: `${page.tasks.length}+`,
								),
							),
							tabs.panel("tasks", () =>
								liveList(
									h,
									shared,
									model.tasks,
									(page) =>
										page.tasks.map((task) =>
											taskRow(h, shared, task, model.tasksReadAt),
										),
									{
										empty: i18next.t("record.tabs.noTasks"),
										failed: i18next.t("record.tabs.tasksFailed"),
									},
									(page) =>
										page.cursor === null
											? null
											: h.p(
													[h.Class("text-sm text-base-content/60 mt-2")],
													[
														i18next.t("record.tabs.moreTasks", {
															count: page.tasks.length,
														}),
													],
												),
								),
							),
							tabs.tab(
								"notes",
								countedLabel(
									i18next.t("record.tabs.notes"),
									model.backlinks,
									(list) => list.backlinks.length,
								),
							),
							tabs.panel("notes", () =>
								liveList(
									h,
									shared,
									model.backlinks,
									(list) =>
										list.backlinks.map((note) => backlinkRow(h, shared, note)),
									{
										empty: i18next.t("record.tabs.noNotes"),
										failed: i18next.t("record.tabs.notesFailed"),
									},
								),
							),
						]
					: []),
			]),
		],
	);
});
